import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../lib/apiClient';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

// Product types available
type ProductType = 'tshirt' | 'totebag' | 'poster' | 'mug' | 'hoodie';

// Basic image response structure
interface IdeogramImage {
  prompt: string;
  resolution: string;
  is_image_safe?: boolean;
  url: string;
}

// Extended interface with product type
interface PrintOnDemandImage extends IdeogramImage {
  productType: ProductType;
}

// API response format
interface PrintOnDemandApiResponse {
  created: string;
  data: PrintOnDemandImage[];
  success?: boolean;
  warning?: string;
  error?: string;
}

const PrintOnDemandGenerator: React.FC = () => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [productType, setProductType] = useState<ProductType>('tshirt');
  const [size, setSize] = useState('1024');
  const [generatedImages, setGeneratedImages] = useState<PrintOnDemandImage[]>([]);
  const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null);

  // Authentication check
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);
  
  // Function to get prompt suggestions based on product type
  const getPromptPlaceholder = (productType: ProductType): string => {
    switch (productType) {
      case 'tshirt':
        return "A vibrant design for a t-shirt with a cosmic space theme, featuring planets and galaxies";
      case 'poster':
        return "A minimalist art deco style poster with geometric shapes in teal and gold";
      case 'totebag':
        return "A stylish pattern for a tote bag with botanical elements and subtle pastel colors";
      case 'mug':
        return "A fun illustration for a coffee mug with mountain scenery and morning sunrise";
      case 'hoodie':
        return "A trendy hoodie design with abstract urban art elements in blue and purple";
      default:
        return "Describe your ideal design";
    }
  };

  // Function to get product icon
  const getProductIcon = (product: ProductType) => {
    switch (product) {
      case 'tshirt':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 8l-4-4H8L4 8l2 4v8h12v-8l2-4z" />
          </svg>
        );
      case 'poster':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'totebag':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
        );
      case 'mug':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'hoodie':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
    }
  };

  // Handle downloading a design image
  const handleDownload = useCallback(async (url: string, index: number) => {
    try {
      setDownloadingIndex(index);
      
      // Create a URL object to check if this is an external URL
      const urlObj = new URL(url);
      const isExternalUrl = urlObj.hostname !== window.location.hostname && !urlObj.hostname.includes('localhost');
      
      if (isExternalUrl) {
        // For Ideogram URLs, we need to create a temporary anchor and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `design-${productType}-${Date.now()}.png`);
        link.setAttribute('target', '_blank');
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('Design download started!');
      } else {
        // For internal URLs without CORS issues, use the fetch + blob approach
        const response = await fetch(url);
        const blob = await response.blob();
        
        // Create a safe filename
        const filename = `design-${productType}-${Date.now()}.png`;
        
        // Save the file using file-saver
        saveAs(blob, filename);
        toast.success('Design downloaded successfully!');
      }
    } catch (error) {
      console.error('Error downloading design:', error);
      toast.error('Failed to download design. Please try again.');
    } finally {
      setDownloadingIndex(null);
    }
  }, [productType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!prompt.trim()) {
      toast.error('Please enter a design description');
      return;
    }
    
    // Clear any existing toasts to prevent duplicates
    toast.dismiss();
    
    setLoading(true);
    
    // Create a unique ID for the loading toast
    const generatingToastId = 'generating-designs-toast';
    
    try {
      // Show generating toast with a specific ID so we can dismiss it later
      toast.loading('Generating designs...', { id: generatingToastId });
      
      // Get userId from auth context
      const userId = await getToken();
      
      if (!userId) {
        toast.dismiss(generatingToastId);
        toast.error('Authentication error. Please log in again.');
        navigate('/login');
        return;
      }
      
      console.log('Making API call to generate designs with:', { 
        productType, 
        promptLength: prompt.length,
        size: parseInt(size),
        userId
      });
      
      // Use centralized API client with automatic auth
      const data = await apiClient.post<PrintOnDemandApiResponse>(
        '/marketing/print-on-demand',
        {
          userId,
          prompt,
          productType,
          size: parseInt(size)
        }
      );
      
      console.log('API response received:', data);
      
      // Always dismiss the specific loading toast by ID
      toast.dismiss(generatingToastId);
      
      if ('data' in data && Array.isArray(data.data) && data.data.length > 0) {
        setGeneratedImages(data.data);
        console.log('Designs set successfully:', data.data.length, 'designs');
        toast.success('Designs generated successfully!');
      } 
      // Handle warning but still got images case
      else if ('data' in data && Array.isArray(data.data) && data.data.length === 0 && data.warning) {
        setGeneratedImages([]);
        toast.error(data.warning || 'Generated designs had issues');
      } 
      // Handle error case
      else {
        console.error('API response format issue:', data);
        toast.error(data.error || 'Failed to generate designs');
      }
    } catch (error: any) {
      // Ensure toast is dismissed even in error cases
      toast.dismiss(generatingToastId);
      console.error('Error generating designs:', error);
      
      // Show user-friendly error message
      const errorMessage = error.message || 'Failed to connect to the design generation service';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-10 relative overflow-hidden">
      {/* Decorative blurred orbs */}
      <div className="absolute top-40 -left-24 w-72 h-72 bg-gradient-to-br from-purple-200 to-blue-300 rounded-full opacity-20 blur-3xl"></div>
      <div className="absolute -top-20 right-10 w-96 h-96 bg-gradient-to-br from-blue-200 to-purple-300 rounded-full opacity-20 blur-3xl"></div>
      
      <div className="relative z-10 mb-10">
        <h1 className="text-4xl font-extrabold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Print-on-Demand Designer</h1>
        <p className="text-gray-700 text-lg max-w-3xl">
          Create custom designs for t-shirts, posters, tote bags and more with AI-powered image generation.
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-2xl shadow-xl p-8">
          {/* Subtle gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-70"></div>
          
          <div className="relative z-10">
            <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-6">Generate Design</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                  Product Type <span className="text-blue-500">*</span>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {['tshirt', 'totebag', 'poster', 'mug', 'hoodie'].map(product => (
                    <button
                      key={product}
                      type="button"
                      onClick={() => setProductType(product as ProductType)}
                      className={`p-4 flex flex-col items-center justify-center rounded-xl transition-all ${
                        productType === product 
                          ? 'bg-gradient-to-br from-blue-100 to-purple-100 border-2 border-blue-400'
                          : 'bg-white/30 backdrop-blur-sm border border-white/40 hover:bg-white/50'
                      }`}
                    >
                      <div className="h-10 w-10 mb-2 flex items-center justify-center">
                        {getProductIcon(product as ProductType)}
                      </div>
                      <span className="text-sm font-medium capitalize">{product}</span>
                    </button>
                  ))}
                </div>
              </div>
            
              <div className="mb-6">
                <label htmlFor="prompt" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                  Design Description <span className="text-blue-500">*</span>
                </label>
                <textarea
                  id="prompt"
                  className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors min-h-[120px]"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={getPromptPlaceholder(productType)}
                  required
                />
                <p className="text-xs text-gray-500 mt-2">Be as specific as possible about colors, style, theme, and any images or text you'd like to see.</p>
              </div>
              
              <div className="mb-6">
                <label htmlFor="size" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                  Image Resolution <span className="text-blue-500">*</span>
                </label>
                <select
                  id="size"
                  className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  required
                >
                  <option value="512">Small (512×512)</option>
                  <option value="1024">Standard (1024×1024)</option>
                  <option value="2048">High Resolution (2048×2048)</option>
                </select>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 px-6 border border-transparent rounded-xl shadow-lg text-white font-medium text-base bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-200 transform hover:scale-[1.02] ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </div>
                ) : (
                  'Generate Designs'
                )}
              </button>
            </form>
          </div>
        </div>
        
        <div>
          {generatedImages.length > 0 ? (
            <div className="relative backdrop-blur-sm bg-white/20 border border-white/30 rounded-2xl shadow-xl p-8 overflow-hidden">
              {/* Subtle gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-70"></div>
              
              <div className="relative z-10">
                <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-6">Generated Designs</h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {generatedImages.map((image, index) => (
                    <div
                      key={`design-${index}`}
                      className="group relative backdrop-blur-sm bg-white/40 border border-white/40 rounded-xl overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02]"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 to-purple-100/10 opacity-40"></div>
                      <div className="relative">
                        <img
                          src={image.url}
                          alt={`Generated ${image.productType} design`}
                          className="w-full h-auto"
                        />
                        <div className="p-4 backdrop-blur-sm bg-white/30">
                          {/* Product type badge */}
                          <div className="mb-3">
                            <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                              {image.productType}
                            </span>
                          </div>
                          
                          <button
                            className="w-full py-2 px-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg text-sm font-medium hover:from-blue-600 hover:to-purple-600 transition-all duration-200 shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-70 disabled:cursor-not-allowed"
                            onClick={() => handleDownload(image.url, index)}
                            disabled={downloadingIndex === index}
                          >
                            {downloadingIndex === index ? (
                              <div className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Downloading...
                              </div>
                            ) : 'Download Design'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 p-4 backdrop-blur-sm bg-white/30 border border-white/30 rounded-xl">
                  <h3 className="text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">Prompt Used:</h3>
                  <p className="text-sm text-gray-600">{generatedImages[0]?.prompt}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative backdrop-blur-sm bg-white/20 border border-white/30 rounded-2xl shadow-lg p-10 text-center overflow-hidden">
              {/* Subtle gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-70"></div>
              
              <div className="relative z-10">
                <div className="mx-auto w-16 h-16 mb-4 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-purple-100 opacity-60">
                  <svg className="w-10 h-10 text-indigo-500 opacity-80" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                
                <h3 className="text-xl font-medium text-gray-700 mb-2">No Designs Generated Yet</h3>
                <p className="text-gray-500 mb-6">Select a product type, enter a design description, and click "Generate Designs" to create custom product designs.</p>
                
                <div className="flex flex-wrap justify-center gap-2">
                  <div className="px-3 py-2 bg-blue-50 text-blue-600 text-sm rounded-lg">
                    "Cosmic space theme with planets and galaxies"
                  </div>
                  <div className="px-3 py-2 bg-purple-50 text-purple-600 text-sm rounded-lg">
                    "Abstract geometric pattern in blue and orange"
                  </div>
                  <div className="px-3 py-2 bg-indigo-50 text-indigo-600 text-sm rounded-lg">
                    "Minimalist mountain landscape with sunset"
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrintOnDemandGenerator;
